
import {Component, Watch} from "vue-property-decorator";
import {getModule} from "vuex-module-decorators";
import {CustomerModule, ShopModule, store, TradeInModule} from "@returns-centre/store";
import _ from 'lodash';
import {mixins} from 'vue-class-component';
import {FormatDateMixin} from '@returns-centre/mixins';
import AppLookupButton from "@returns-centre/components/AppLookupButton.vue";
import TradeInOrderLineQuestionsModal from "./TradeInOrderLineQuestionsModal.vue";

@Component
({
    components: {
        AppLookupButton,
        TradeInOrderLineQuestionsModal,
    }
})
export default class TradeInOrders extends mixins(FormatDateMixin) {
    customerModule = getModule(CustomerModule, store);
    shopModule = getModule(ShopModule, store);
    tradeInModule = getModule(TradeInModule, store);

    selectedOrderLineItems: { [key: string]: OrderLineItem } = {}
    wantToSelectItem: OrderLineItem|null = null;

    get sortedOrderLineItems(): OrderLineItem[] {
        let lineItems: OrderLineItem[] = [];
        const quantityList: OrderLineItem[] = [];

        this.customerModule.getOrders?.forEach((order: Order) => {
            lineItems = [...lineItems, ...order.line_items];
        });

        lineItems.forEach((item) => {
            _.range(item.quantity - item.refunded_quantity - item.fulfillable_quantity).forEach((index) => {
                quantityList.push({
                    ...item,
                    fulfillment_status: item.fulfillment_status === null ? null : 'fulfilled',
                    key: `${item.id}_${index}`,
                    is_returnable: this.isReturnable(item, index),
                    index,
                });
            });
        });

        return quantityList
            .sort((a, b) => new Date(b.order_date as string).valueOf() - new Date(a.order_date as string).valueOf())
            .sort((a, b) => Number(!b.trade_in_offer.is_available_for_return) - Number(!a.trade_in_offer.is_available_for_return))
            .sort((a, b) => Number(b.is_returnable) - Number(a.is_returnable));
    }

    get questions(): RegisteredProductQuestion[] {
        return this.shopModule.shop?.registered_product_questions?.filter((item) => item.use_for_order_history) || [];
    }

    wantToSelect(lineItem: OrderLineItem) {
        const id: string = lineItem.key ?? lineItem.id + '_' + 0;
        if (_.hasIn(this.selectedOrderLineItems, id)) {
            this.selectedOrderLineItems = _.omit(this.selectedOrderLineItems, [id]);

            this.tradeInModule.setSelectedOrderLineItems(this.selectedOrderLineItems);
            this.$emit('setOrderLineItem',this.selectedOrderLineItems);
        } else {
            if(!this.questions.length) {
                this.setOrderLineItem(lineItem);
                return;
            }

            this.wantToSelectItem = lineItem;
        }
    }

    setOrderLineItem(lineItem: OrderLineItem, index: number = 0) {
        if (this.wantToSelectItem && this.wantToSelectItem === lineItem) {
            this.wantToSelectItem = null;
        }

        // if order is fulfilled
        if (lineItem.fulfillment_status === null) {
            return;
        }

        // Already returned if no available quantity
        if (_.toNumber(lineItem.trade_in_available_quantity) <= 0) {
            return;
        }

        /**
         Because we are display by singly quantity
         and items could have been already returned,
         so only allow the available products selection
         */
        if ((lineItem.index ?? index) >= lineItem.trade_in_available_quantity) {
            return;
        }

        // if trade-in profiles deny returns
        if (!lineItem.trade_in_offer.is_available_for_return) {
            return;
        }

        const id: string = lineItem.key ?? lineItem.id + '_' + index;
        if (_.hasIn(this.selectedOrderLineItems, id)) {
            this.selectedOrderLineItems = _.omit(this.selectedOrderLineItems, [id]);
        } else {
            this.$set(this.selectedOrderLineItems, id, lineItem);
        }
        this.tradeInModule.setSelectedOrderLineItems(this.selectedOrderLineItems);
        this.$emit('setOrderLineItem',this.selectedOrderLineItems);
    }

    next() {
        this.$emit('next');
    }

    get hasSelectedOrderLineItem() {
        return (id: string) => {
            return _.hasIn(this.selectedOrderLineItems, id)
                ? true
                : _.hasIn(this.tradeInModule.selected_order_line_items, id)

        }
    }

    async created() {
        this.tradeInModule.reset();
        this.tradeInModule.resetLocalStorage();

        if (this.customerModule.hasLoggedInUser) {
            await this.customerModule.fetchOrders(this.customerModule.loggedInUser);
        }
        if (this.tradeInModule.selected_order_line_items) {
            this.selectedOrderLineItems = this.tradeInModule.selected_order_line_items;
        }
    }

    isReturnable(lineItem: OrderLineItem, index: number): boolean {

        return lineItem.trade_in_offer.is_available_for_return
            && (lineItem.index ?? index) < lineItem.trade_in_available_quantity
            && lineItem.fulfillment_status !== null;
    }

    @Watch('tradeInModule.selected_order_line_items')
    onTradeInModuleSelectedOrderLineItemsChanged(items: { [key: string]: OrderLineItem }) {
        this.selectedOrderLineItems = items;
    }
}
